import React from 'react';

import { AlloySuspendable, AlloySuspense } from '@adsk/alloy-react-suspense';

import { SuspenseFallback } from './layout';
import AutocompleteTemplate, {
  type AutocompleteTemplateProps,
} from './templates/Autocomplete';
import DropdownTemplate, {
  type DropdownTemplateProps,
} from './templates/Dropdown';
import MultiAutocompleteTemplate, {
  type MultiAutocompleteTemplateProps,
} from './templates/MultiAutocomplete';
import MultiDropdownTemplate, {
  type MultiDropdownTemplateProps,
} from './templates/MultiDropdown';
import type { GenericItem } from './types';

type AllAutocompleteProps<TItem extends GenericItem> =
  AutocompleteTemplateProps<TItem> & {
    isAutocomplete: true;
    isMultiple?: never;
  };

type AllDropdownProps<TItem extends GenericItem> =
  DropdownTemplateProps<TItem> & {
    isAutocomplete?: never;
    isMultiple?: never;
  };

type AllMultiAutocompleteProps<TItem extends GenericItem> =
  MultiAutocompleteTemplateProps<TItem> & {
    isAutocomplete: true;
    isMultiple: true;
  };

type AllMultiDropdownProps<TItem extends GenericItem> =
  MultiDropdownTemplateProps<TItem> & {
    isAutocomplete?: never;
    isMultiple: true;
  };

export type DropdownProps<TItem extends GenericItem> = (
  | AutocompleteTemplateProps<TItem>
  | DropdownTemplateProps<TItem>
  | MultiAutocompleteTemplateProps<TItem>
  | MultiDropdownTemplateProps<TItem>
) &
  AlloySuspendable & {
    /**
     * Enable autocomplete/search
     */
    isAutocomplete?: boolean;
    /**
     * Enable multiple selection
     */
    isMultiple?: boolean;
    /**
     * Disable the entire dropdown
     */
    disabled?: boolean;
    /**
     * Enable error state
     */
    error?: boolean;
    /**
     * Enable loading state
     */
    isLoading?: boolean;
    /**
     * Placeholder text to display
     */
    placeholder?: string;
    /**
     * Placeholder text to display in the search input when the dropdown is open or focused (only applies to autocomplete variants)
     */
    searchPlaceholder?: string;
    /**
     * Enable highlighting of search text in menu items (only applies to autocomplete variants)
     */
    enableSearchHighlight?: boolean;
  };

const isAutocomplete = <TItem extends GenericItem>(
  props: DropdownProps<TItem>,
): props is AllAutocompleteProps<TItem> =>
  !!props.isAutocomplete && !props.isMultiple;

const isDropdown = <TItem extends GenericItem>(
  props: DropdownProps<TItem>,
): props is AllDropdownProps<TItem> =>
  !props.isAutocomplete && !props.isMultiple;

const isMultiAutocomplete = <TItem extends GenericItem>(
  props: DropdownProps<TItem>,
): props is AllMultiAutocompleteProps<TItem> =>
  !!props.isAutocomplete && !!props.isMultiple;

const isMultiDropdown = <TItem extends GenericItem>(
  props: DropdownProps<TItem>,
): props is AllMultiDropdownProps<TItem> =>
  !props.isAutocomplete && !!props.isMultiple;

const Dropdown = <TItem extends GenericItem>({
  emitSuspenseEvents,
  ...props
}: DropdownProps<TItem>) => {
  let render = null;
  if (isAutocomplete(props)) {
    render = () => <AutocompleteTemplate {...props} />;
  }
  if (isDropdown(props)) {
    render = () => <DropdownTemplate {...props} />;
  }

  if (isMultiAutocomplete(props)) {
    render = () => <MultiAutocompleteTemplate {...props} />;
  }
  if (isMultiDropdown(props)) {
    render = () => <MultiDropdownTemplate {...props} />;
  }

  return render ? (
    <AlloySuspense
      fallback={<SuspenseFallback />}
      emitSuspenseEvents={emitSuspenseEvents}
    >
      {render()}
    </AlloySuspense>
  ) : null;
};

export default Dropdown;
